import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { apiRequest } from "@/lib/queryClient";
import { Transaction, TransactionCategory } from "@shared/schema";
import { useCurrentBusiness } from "./use-businesses";

interface TransactionQueryParams {
  startDate?: string;
  endDate?: string;
  type?: string;
  categoryId?: number;
  page?: number;
  limit?: number;
  status?: string;
}

type TransactionInput = Omit<Partial<Transaction>, "date"> & { date?: Date | string };

export const useTransactions = (businessIdOrParams?: number | TransactionQueryParams, paramsArg?: TransactionQueryParams) => {
  const queryClient = useQueryClient();
  const { currentBusiness } = useCurrentBusiness();
  // Debug logging removed to prevent render cascade
  
  // Get business ID from URL if present (prioritize this over anything else)
  const urlParams = new URLSearchParams(window.location.search);
  const urlBusinessId = urlParams.get('business');
  const urlBusinessIdNumber = urlBusinessId ? parseInt(urlBusinessId, 10) : null;
  
  // Handle flexible parameter order
  let businessId: number | undefined;
  let params: TransactionQueryParams = {};
  
  if (typeof businessIdOrParams === 'number') {
    // First param is businessId, second is params
    businessId = businessIdOrParams;
    params = paramsArg || {};
  } else if (typeof businessIdOrParams === 'object') {
    // First param is params object
    businessId = undefined;
    params = businessIdOrParams || {};
  }
  
  // Priority order for business ID:
  // 1. URL parameter (highest priority)
  // 2. Passed businessId parameter
  // 3. Current business from context
  const effectiveBusinessId = urlBusinessIdNumber || businessId || currentBusiness?.id;
  
  // Construct query string from params
  const queryParams = new URLSearchParams();
  if (params.startDate) queryParams.append("startDate", params.startDate);
  if (params.endDate) queryParams.append("endDate", params.endDate);
  if (params.type) queryParams.append("type", params.type);
  if (params.categoryId) queryParams.append("categoryId", params.categoryId.toString());
  if (params.page) queryParams.append("page", params.page.toString());
  if (params.limit) queryParams.append("limit", params.limit.toString());
  if (params.status) queryParams.append("status", params.status);
  
  const queryString = queryParams.toString();
  const endpoint = effectiveBusinessId 
    ? `/api/businesses/${effectiveBusinessId}/transactions${queryString ? `?${queryString}` : ""}`
    : "";
    
  // For debugging
  // Debug logging removed to prevent render cascade
  
  // Fetch transactions
  const { data, isLoading, error, refetch } = useQuery<{
    transactions: Transaction[];
    total: number;
  }>({
    // Make sure the ENTIRE business ID is part of the query key
    queryKey: [`/api/businesses/${effectiveBusinessId}/transactions`, effectiveBusinessId, params],
    queryFn: async () => {
      if (!effectiveBusinessId) {
        return { transactions: [], total: 0 };
      }
      // Always rebuild the endpoint with the current businessId before making the request
      const currentEndpoint = `/api/businesses/${effectiveBusinessId}/transactions${queryString ? `?${queryString}` : ""}`;
      // Fetch silently — debug logs removed
      
      try {
        const response = await fetch(currentEndpoint, {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            "Cache-Control": "no-cache",
            "Pragma": "no-cache"
          },
          credentials: "include"
        });
        
        if (!response.ok) {
          throw new Error(`HTTP ${response.status}: ${response.statusText}`);
        }
        
        const data = await response.json();
        
        // Handle both array and object responses
        if (Array.isArray(data)) {
          console.log(`✅ Fetched ${data.length} transactions for business ${effectiveBusinessId}`);
          return { transactions: data, total: data.length };
        } else if (data && data.transactions) {
          console.log(`✅ Fetched ${data.transactions.length} transactions for business ${effectiveBusinessId}`);
          return data;
        } else {
          console.warn("⚠️ Unexpected response format:", data);
          return { transactions: [], total: 0 };
        }
      } catch (error) {
        console.error(`❌ Error fetching transactions for business ${effectiveBusinessId}:`, error);
        return { transactions: [], total: 0 };
      }
    },
    enabled: !!effectiveBusinessId,
    // Force refetch when business ID changes and prevent any caching between businesses
    staleTime: 0,
    gcTime: 0, // Clear cache immediately after query becomes inactive
    // Prevent caching cross-business data
    refetchOnWindowFocus: true,
    refetchOnMount: true,
    refetchOnReconnect: true,
    // Force fresh data every time
    refetchInterval: false
  });
  
  // Create transaction
  const { mutateAsync: createTransaction, isPending: isPendingCreate } = useMutation({
    mutationFn: async (transaction: TransactionInput) => {
      if (!effectiveBusinessId) throw new Error("No active business selected");
      
      try {
        const data = {
          ...transaction,
        };
        
        // Make API request
        const response = await fetch(`/api/businesses/${effectiveBusinessId}/transactions`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify(data),
          credentials: 'include'
        });
        
        // Return the full response, not just the data
        // This allows the component to handle error cases specifically
        return response;
      } catch (error) {
        console.error('Error creating transaction:', error);
        throw error;
      }
    },
    onSuccess: (response) => {
      // Only invalidate queries if the response was successful
      if (response.ok) {
        queryClient.invalidateQueries({
        predicate: (query) => {
          const key = query.queryKey[0];
          return typeof key === 'string' && key.includes(`/api/businesses/${effectiveBusinessId}/transactions`);
        },
      });
      }
    },
  });
  
  // Update transaction
  const { mutateAsync: updateTransaction, isPending: isPendingUpdate } = useMutation({
    mutationFn: async ({ id, ...data }: { id: number } & Partial<Transaction>) => {
      if (!effectiveBusinessId) throw new Error("No active business selected");
      const response = await apiRequest("PATCH", `/api/businesses/${effectiveBusinessId}/transactions/${id}`, {
        ...data,
      });
      return response.json();
    },
    onSuccess: () => {
      // Invalidate ALL transaction queries for this business (any filter/page)
      queryClient.invalidateQueries({
        predicate: (query) => {
          const key = query.queryKey[0];
          return typeof key === 'string' && key.includes(`/api/businesses/${effectiveBusinessId}/transactions`);
        },
      });
    },
  });
  
  // Delete transaction
  const { mutateAsync: deleteTransaction, isPending: isPendingDelete } = useMutation({
    mutationFn: async (id: number) => {
      if (!effectiveBusinessId) throw new Error("No active business selected");
      return apiRequest("DELETE", `/api/businesses/${effectiveBusinessId}/transactions/${id}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        predicate: (query) => {
          const key = query.queryKey[0];
          return typeof key === 'string' && key.includes(`/api/businesses/${effectiveBusinessId}/transactions`);
        },
      });
    },
  });
  
  // Import CSV
  const { mutateAsync: importCSV, isPending: isPendingCSVImport } = useMutation({
    mutationFn: async (formData: FormData) => {
      if (!effectiveBusinessId) throw new Error("No active business selected");
      
      try {
        // Use fetch directly to handle non-ok responses like with categorizeTransactions
        const response = await fetch(`/api/businesses/${effectiveBusinessId}/transactions/import/csv`, {
          method: 'POST',
          body: formData,
          credentials: 'include'
        });
        
        // Return the full response to allow specific error handling
        return response;
      } catch (error: any) {
        console.error("CSV import error:", error);
        
        if (error.name === 'TypeError' && error.message.includes('Failed to fetch')) {
          throw new Error("Network error. Please check your connection and try again.");
        } else {
          // If it's a generic error without response details
          throw new Error(error.message || "Failed to import CSV. Please check your file format and try again.");
        }
      }
    },
    onSuccess: (response) => {
      // Only invalidate queries if the response was successful
      if (response.ok) {
        queryClient.invalidateQueries({
        predicate: (query) => {
          const key = query.queryKey[0];
          return typeof key === 'string' && key.includes(`/api/businesses/${effectiveBusinessId}/transactions`);
        },
      });
      }
    },
  });
  
  // Import QBO/OFX financial file
  const { mutateAsync: importFinancialFile, isPending: isPendingFinancialFileImport } = useMutation({
    mutationFn: async (formData: FormData) => {
      if (!effectiveBusinessId) throw new Error("No active business selected");
      
      try {
        // Use fetch directly to handle non-ok responses
        const response = await fetch(`/api/businesses/${effectiveBusinessId}/transactions/import/financial`, {
          method: 'POST',
          body: formData,
          credentials: 'include'
        });
        
        // Return the full response to allow specific error handling
        return response;
      } catch (error: any) {
        console.error("Financial file import error:", error);
        
        if (error.name === 'TypeError' && error.message.includes('Failed to fetch')) {
          throw new Error("Network error. Please check your connection and try again.");
        } else {
          // If it's a generic error without response details
          throw new Error(error.message || "Failed to import financial file. Please check your file format and try again.");
        }
      }
    },
    onSuccess: (response) => {
      // Only invalidate queries if the response was successful
      if (response.ok) {
        queryClient.invalidateQueries({
        predicate: (query) => {
          const key = query.queryKey[0];
          return typeof key === 'string' && key.includes(`/api/businesses/${effectiveBusinessId}/transactions`);
        },
      });
      }
    },
  });
  
  // Plaid functions
  const { mutateAsync: createPlaidLinkToken, isPending: isPendingCreatePlaidToken } = useMutation({
    mutationFn: async () => {
      if (!effectiveBusinessId) throw new Error("No active business selected");
      return apiRequest("POST", `/api/businesses/${effectiveBusinessId}/plaid/link-token`);
    },
  });
  
  const { mutateAsync: exchangePlaidToken, isPending: isPendingExchangeToken } = useMutation({
    mutationFn: async (publicToken: string) => {
      if (!effectiveBusinessId) throw new Error("No active business selected");
      return apiRequest("POST", `/api/businesses/${effectiveBusinessId}/plaid/exchange-token`, { publicToken });
    },
  });
  

  
  const { mutateAsync: importPlaidTransactions, isPending: isPendingPlaidImport } = useMutation({
    mutationFn: async ({ startDate, endDate }: { startDate: string, endDate: string }) => {
      if (!effectiveBusinessId) throw new Error("No active business selected");
      
      console.log("Importing Plaid transactions:", { businessId: effectiveBusinessId, startDate, endDate });
      
      try {
        const response = await fetch(`/api/businesses/${effectiveBusinessId}/transactions/import/plaid`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({ startDate, endDate }),
          credentials: 'include'
        });
        
        // Return the full response to allow specific error handling
        return response;
      } catch (error: any) {
        console.error("Error importing Plaid transactions:", error);
        if (error.name === 'TypeError' && error.message.includes('Failed to fetch')) {
          throw new Error("Network error. Please check your connection and try again.");
        }
        throw new Error(error.message || "Failed to import transactions. Please try again.");
      }
    },
    onSuccess: (response) => {
      // Invalidate all related queries so KPIs and sync time update
      if (response.ok) {
        queryClient.invalidateQueries({
        predicate: (query) => {
          const key = query.queryKey[0];
          return typeof key === 'string' && key.includes(`/api/businesses/${effectiveBusinessId}/transactions`);
        },
      });
        queryClient.invalidateQueries({ queryKey: ["plaid-connection-status", effectiveBusinessId] });
        queryClient.invalidateQueries({ queryKey: [`/api/businesses/${effectiveBusinessId}/plaid/connection-status`] });
      }
    },
    onError: (error: any) => {
      console.error("Transaction import error:", error);
    }
  });

  // AI Categorization
  const { mutateAsync: categorizeTransactions, isPending: isPendingCategorize } = useMutation({
    mutationFn: async (transactionIds: number[]) => {
      if (!effectiveBusinessId) throw new Error("No active business selected");
      
      // Use fetch directly to be able to handle non-ok responses
      const response = await fetch(`/api/businesses/${effectiveBusinessId}/transactions/categorize`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ transactionIds }),
        credentials: 'include'
      });
      
      return response;
    },
    onSuccess: async (response) => {
      // Only invalidate queries if the response was successful
      if (response.ok) {
        // Get the updated transactions from the response
        const responseData = await response.json();
        
        // Force a full invalidation of all transaction queries for this business
        queryClient.invalidateQueries({
          queryKey: [`/api/businesses/${effectiveBusinessId}/transactions`]
        });
        
        // Also invalidate any transaction listings with filters
        queryClient.invalidateQueries({
          predicate: (query) => {
            const queryKeyStr = query.queryKey[0]?.toString() || '';
            return queryKeyStr.includes(`/api/businesses/${effectiveBusinessId}/transactions`);
          }
        });
        
        console.log('AI categorization completed and cache updated for transactions');
        
        // Return the processed data for component use
        return responseData;
      }
      return response;
    },
  });

  // Check if business has Plaid connection
  const { data: plaidStatus, isLoading: isLoadingPlaidStatus, refetch: refetchPlaidStatus } = useQuery<{
    connected: boolean;
    connectionDate: string | null;
    lastSyncedAt: string | null;
  }>({
    queryKey: ["plaid-connection-status", effectiveBusinessId],
    queryFn: async () => {
      if (!effectiveBusinessId) {
        return { connected: false, connectionDate: null };
      }
      try {
        const response = await apiRequest("GET", `/api/businesses/${effectiveBusinessId}/plaid/connection-status`);
        return await response.json();
      } catch (error) {
        console.error("Error fetching Plaid connection status:", error);
        return { connected: false, connectionDate: null };
      }
    },
    enabled: !!effectiveBusinessId,
  });

  // Split Transaction
  const { mutateAsync: splitTransaction, isPending: isPendingSplit } = useMutation({
    mutationFn: async ({
      transactionId,
      splitEntries
    }: {
      transactionId: number;
      splitEntries: Array<{
        description: string;
        amount: string | number;
        categoryId?: number | null;
      }>;
    }) => {
      if (!effectiveBusinessId) throw new Error("No active business selected");
      
      try {
        // Log the request details for debugging
        console.log(`Splitting transaction ${transactionId} for business ${effectiveBusinessId}:`, { splitEntries });
        
        const response = await fetch(`/api/businesses/${effectiveBusinessId}/transactions/${transactionId}/split`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({ splitEntries }),
          credentials: 'include'
        });
        
        // Return the full response to allow specific error handling
        return response;
      } catch (error: any) {
        console.error("Error splitting transaction:", error);
        throw new Error(error.message || "Failed to split transaction. Please try again.");
      }
    },
    onSuccess: (response) => {
      // Only invalidate queries if the response was successful
      if (response.ok) {
        // Invalidate the specific transaction query
        queryClient.invalidateQueries({ queryKey: [`/api/businesses/${effectiveBusinessId}/transactions`, effectiveBusinessId] });
        
        // Also invalidate any specific transaction data that might be cached
        queryClient.invalidateQueries({ 
          predicate: (query) => {
            return String(query.queryKey[0]).includes(`/api/businesses/${effectiveBusinessId}/transactions/`)
          }
        });
      }
    },
  });

  // Exclude transaction
  const { mutateAsync: excludeTransaction, isPending: isPendingExclude } = useMutation({
    mutationFn: async (id: number) => {
      if (!effectiveBusinessId) throw new Error("No active business selected");
      return apiRequest("POST", `/api/businesses/${effectiveBusinessId}/transactions/${id}/exclude`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [`/api/businesses/${effectiveBusinessId}/transactions`, effectiveBusinessId] });
    },
  });

  // Restore excluded transaction
  const { mutateAsync: restoreTransaction, isPending: isPendingRestore } = useMutation({
    mutationFn: async (id: number) => {
      if (!effectiveBusinessId) throw new Error("No active business selected");
      return apiRequest("POST", `/api/businesses/${effectiveBusinessId}/transactions/${id}/restore`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [`/api/businesses/${effectiveBusinessId}/transactions`, effectiveBusinessId] });
    },
  });

  return {
    transactions: data?.transactions || [],
    total: data?.total || 0,
    isLoading,
    error,
    createTransaction,
    updateTransaction,
    deleteTransaction,
    excludeTransaction,
    restoreTransaction,
    importCSV,
    importFinancialFile,
    createPlaidLinkToken,
    exchangePlaidToken,
    importPlaidTransactions,
    categorizeTransactions,
    splitTransaction,
    isPendingCreate,
    isPendingUpdate,
    isPendingDelete,
    isPendingExclude,
    isPendingRestore,
    isPendingCSVImport,
    isPendingFinancialFileImport,
    isPendingCreatePlaidToken,
    isPendingExchangeToken,
    isPendingPlaidImport,
    isPendingCategorize,
    isPendingSplit,
    refetch,
    // Business context
    effectiveBusinessId,
    // Plaid connection status
    isPlaidConnected: plaidStatus?.connected || false,
    plaidConnectionDate: plaidStatus?.connectionDate,
    plaidLastSyncedAt: plaidStatus?.lastSyncedAt || null,
    isLoadingPlaidStatus,
    refetchPlaidStatus,
  };
};

export const useTransactionCategories = () => {
  const queryClient = useQueryClient();
  const { currentBusiness } = useCurrentBusiness();
  const businessId = currentBusiness?.id;

  // Fetch categories from COA (business-scoped)
  const { data: categories, isLoading } = useQuery<any[]>({
    queryKey: ["transaction-categories", businessId],
    queryFn: async () => {
      if (!businessId) return [];
      try {
        const response = await fetch(`/api/businesses/${businessId}/categories`, {
          credentials: "include",
        });
        if (!response.ok) return [];
        const data = await response.json();
        return Array.isArray(data) ? data : [];
      } catch {
        return [];
      }
    },
    enabled: !!businessId,
  });

  // Create category (no-op for now — categories come from COA)
  const { mutateAsync: createCategory, isPending: isPendingCreate } = useMutation({
    mutationFn: async (category: Partial<TransactionCategory>) => {
      return apiRequest("POST", "/api/transaction-categories", category);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["transaction-categories", businessId] });
    },
  });

  return {
    categories: categories || [],
    isLoading,
    createCategory,
    isPendingCreate,
  };
};
